import { unwrap } from "@sagot/api-client";
import { BILLING_INVOICES_PAGE_SIZE } from "@sagot/constants";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { queryKeys } from "../../../lib/query-keys";
import { useDebounced } from "../../../lib/slug";
import { usePermissions } from "../../../session/permissions";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import type { BillingInterval } from "./billing-model";
import { wsKeys, type BillingDetailsOut, type BillingPlan, type InvoiceOut, type PlanKeyOut, type PlanPreview, type UsageBreakdownOut } from "../shared";

export type BreakdownBy = "agent" | "channel" | "day" | "feature";

export function useBillingPlans(enabled = true): UseQueryResult<BillingPlan[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.billingPlans(wsId), queryFn: () => unwrap(api.GET("/v1/billing/plans")), staleTime: 10 * 60_000, enabled });
}

export function useBillingDetails(): UseQueryResult<BillingDetailsOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.billingDetails(wsId), queryFn: () => unwrap(api.GET("/v1/billing/details")) });
}

/** Invoices, searched server-side (`q`, debounced). */
export function useInvoices(search: string): UseQueryResult<InvoiceOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  const q = useDebounced(search.trim());
  return useQuery({
    queryKey: wsKeys.invoices(wsId, q),
    queryFn: () => unwrap(api.GET("/v1/billing/invoices", { params: { query: { limit: BILLING_INVOICES_PAGE_SIZE, ...(q ? { q } : {}) } } })),
    placeholderData: (previous) => previous,
  });
}

export function useUsageBreakdown(by: BreakdownBy): UseQueryResult<UsageBreakdownOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.usageBreakdown(wsId, by), queryFn: () => unwrap(api.GET("/v1/billing/usage/breakdown", { params: { query: { by } } })) });
}

/** Proration preview for a plan change (server-side math, never computed in the browser). */
/** What switching to `planKey` on `interval` would charge, credit and restart (the interval can turn an upgrade into a restart of the period). */
export function usePlanPreview(planKey: PlanKeyOut | null, interval: BillingInterval): UseQueryResult<PlanPreview> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: wsKeys.planPreview(wsId, planKey ?? "", interval),
    queryFn: () => {
      if (!planKey) throw new Error("plan required");
      return unwrap(api.POST("/v1/billing/subscription/preview", { body: { plan_key: planKey, interval, save_method: false } }));
    },
    enabled: planKey !== null,
    staleTime: 0,
  });
}

/** Invalidates every billing query (overview, invoices, plans…) plus nav usage meters. */
export function useInvalidateBilling() {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: wsKeys.billing(wsId) }),
      queryClient.invalidateQueries({ queryKey: ["ws", wsId, "nav"] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.workspace(wsId) }),
    ]);
}

/** `billing.manage` held and not a read-only support session. */
export function useCanManageBilling(): boolean {
  const permissions = usePermissions();
  return permissions.can("billing.manage") && !permissions.readOnly;
}
